"use client";
import React, { useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import logoImg from "../../../public/assets/images/nextgentask-logo.png";
import dashboardImg from "../../../public/assets/images/dashboard-mockup.png";
import heroBannerImg from "../../../public/assets/images/hero-banner.jpg";
import PublicFooter from "@/components/layout/PublicFooter";



const features = [
  {
    name: "Task Management",
    desc: "Organize, assign, and track tasks efficiently across your team.",
    type: "bar",
    style: "vertical",
    chartData: [12, 19, 7, 14, 10],
  },
  {
    name: "Project Management",
    desc: "Plan, execute, and monitor projects with timelines and milestones.",
    type: "table",
    table: [
      ["Project", "Status", "Deadline"],
      ["Website Redesign", "In Progress", "Feb 20"],
      ["Mobile App", "Completed", "Jan 15"],
      ["Marketing", "Pending", "Mar 10"],
    ],
  },

  {
    name: "Organization chart",
    desc: "Visualize your company hierarchy and team structures.",
    type: "table",
    table: [
      ["Role", "Name", "Reports To"],
      ["CEO", "Amit Sharma", "Board of Directors"],
      ["VP of Engineering", "Vikram Patel", "CEO"],
      ["VP of Product", "Sneha Rao", "CEO"],
      ["Tech Lead", "Rahul Mehta", "VP of Engineering"],
    ],
  },
  {
    name: "Employee KRA/KPI",
    desc: "Track key responsibility areas and key performance indicators.",
    type: "table",
    table: [
      ["KRA Category", "KPI Metric", "Target Status"],
      ["Delivery", "On-Time Task Delivery", "95% Target Met"],
      ["Quality", "Code Review Rating", "4.8/5.0 Achieved"],
      ["Innovation", "New Features Proposed", "3 Implemented"],
    ],
  },
  {
    name: "Company Growth",
    desc: "Analyze overall company productivity and project success over time.",
    type: "line",
    chartData: [40, 55, 65, 80, 95],
  },

  {
    name: "Reports",
    desc: "Generate insightful reports for tasks, projects, and teams.",
    type: "bar",
    style: "vertical",
    chartData: [10, 15, 8, 12, 7],
  },


  {
    name: "Task Delegation",
    desc: "Assign tasks to the right people and monitor progress.",
    type: "pie",
    style: "donut",
    pie: [60, 20, 20],
  },
  {
    name: "To Do List",
    desc: "Keep track of daily tasks and priorities.",
    type: "table",
    table: [
      ["Task", "Status"],
      ["Email client", "Done"],
      ["Prepare report", "Pending"],
    ],
  },
  {
    name: "Task Tracker",
    desc: "Track progress and completion rates for all tasks.",
    type: "pie",
    style: "segment",
    pie: [70, 15, 15],
  },

];

const industries = [
  {
    name: "Tech & IT Industry",
    fit: "Align developer sprint tasks with career path goals, mapping code contributions directly to employee KRAs & milestones.",
    desc: "Agile Teams & Dev Shops",
    color: "from-blue-500 to-indigo-500",
    iconPath: "M16 18l6-6-6-6M8 6l-6 6 6 6"
  },
  {
    name: "Law & Legal Services",
    fit: "Track complex cases, documents, and billable hour milestones while monitoring paralegal task delegation and output.",
    desc: "Firms & General Counsel",
    color: "from-purple-500 to-pink-500",
    iconPath: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
  },
  {
    name: "Manufacturing & Operations",
    fit: "Connect supply chain metrics with operational tasks, tracking workforce productivity across multiple facilities.",
    desc: "Factories & Plant Floors",
    color: "from-amber-500 to-orange-500",
    iconPath: "M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"
  },
  {
    name: "Education & EdTech",
    fit: "Coordinate course development schedules and analyze performance benchmarks for academic and administrative staff.",
    desc: "Schools & Online Learning",
    color: "from-emerald-500 to-teal-500",
    iconPath: "M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
  },
  {
    name: "Healthcare & Biotech",
    fit: "Manage compliance audit tasks, equipment planning workflows, and hospital operations without losing data traceability.",
    desc: "Hospitals & Labs",
    color: "from-rose-500 to-red-500",
    iconPath: "M22 12h-4l-3 9L9 3l-3 9H2"
  },
  {
    name: "Event & Agencies",
    fit: "Deliver multi-phase client campaigns, align deadlines with vendor coordination tasks, and keep teams on schedule.",
    desc: "PR, Ads & Design",
    color: "from-cyan-500 to-blue-500",
    iconPath: "M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
  }
];

function FeatureModal({ feature, onClose }: { feature: any; onClose: () => void }) {
  // Pie chart colors
  const pieColors = ["#60a5fa", "#34d399", "#fbbf24", "#818cf8", "#f472b6", "#a3e635", "#f87171", "#f43f5e", "#38bdf8", "#facc15"];

  // Pie chart generator
  function renderPieChart(data: number[], style: string = "segment") {
    const total = data.reduce((a, b) => a + b, 0);
    let cumulative = 0;
    const radius = style === "donut" ? 40 : 48;
    const center = 60;
    return (
      <svg width={120} height={120} viewBox="0 0 120 120">
        <circle cx={center} cy={center} r={radius} fill="#f3f4f6" />
        {style === "donut" && <circle cx={center} cy={center} r={radius - 18} fill="#fff" />}
        {data.map((value, i) => {
          const startAngle = (cumulative / total) * 2 * Math.PI;
          const endAngle = ((cumulative + value) / total) * 2 * Math.PI;
          const x1 = center + radius * Math.cos(startAngle);
          const y1 = center + radius * Math.sin(startAngle);
          const x2 = center + radius * Math.cos(endAngle);
          const y2 = center + radius * Math.sin(endAngle);
          const largeArc = value / total > 0.5 ? 1 : 0;
          const pathData = `M${center},${center} L${x1},${y1} A${radius},${radius} 0 ${largeArc},1 ${x2},${y2} Z`;
          cumulative += value;
          return (
            <path key={i} d={pathData} fill={pieColors[i % pieColors.length]} stroke="#fff" strokeWidth={2} />
          );
        })}
      </svg>
    );
  }

  // Line chart generator
  function renderLineChart(data: number[]) {
    const max = Math.max(...data);
    const points = data.map((val, i) => `${20 + i * 20},${100 - (val / max) * 80}`).join(" ");
    return (
      <svg width={120} height={120} viewBox="0 0 120 120">
        <rect x={0} y={0} width={120} height={120} rx={16} fill="#f3f4f6" />
        <polyline points={points} fill="none" stroke="#60a5fa" strokeWidth={4} strokeLinejoin="round" />
        {data.map((val, i) => (
          <circle key={i} cx={20 + i * 20} cy={100 - (val / max) * 80} r={5} fill={pieColors[i % pieColors.length]} />
        ))}
      </svg>
    );
  }

  // Horizontal bar chart
  function renderHorizontalBarChart(data: number[]) {
    const max = Math.max(...data);
    return (
      <div className="flex flex-col gap-2 mb-4">
        {data.map((val: number, idx: number) => (
          <div key={idx} className="flex items-center gap-2">
            <div className="h-8 rounded-xl bg-gradient-to-r from-primary/30 to-primary/70 shadow-md" style={{ width: `${(val / max) * 120}px`, background: pieColors[idx % pieColors.length] }}></div>
            <span className="text-xs text-primary font-bold">{val}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full relative animate-fade-in">
        <button className="absolute top-3 right-3 text-xl text-muted-foreground hover:text-primary" onClick={onClose}>&times;</button>
        <h3 className="text-2xl font-bold text-primary mb-2 text-center">{feature.name}</h3>
        <p className="text-base text-muted-foreground mb-4 text-center">{feature.desc}</p>
        {feature.type === "bar" && feature.style === "vertical" && (
          <div className="flex items-end justify-center gap-2 h-36 mb-4">
            {feature.chartData.map((val: number, idx: number) => (
              <div key={idx} className="w-10 rounded-xl flex flex-col justify-end items-center shadow-md hover:scale-105 transition" style={{ background: pieColors[idx % pieColors.length] }}>
                <div style={{ height: `${val * 6}px`, background: pieColors[idx % pieColors.length] }} className="w-full rounded-xl"></div>
                <span className="block text-xs text-primary mt-2 font-bold">{val}</span>
              </div>
            ))}
          </div>
        )}
        {feature.type === "bar" && feature.style === "horizontal" && renderHorizontalBarChart(feature.chartData)}
        {feature.type === "pie" && renderPieChart(feature.pie, feature.style)}
        {feature.type === "line" && renderLineChart(feature.chartData)}
        {feature.type === "table" && (
          <div className="overflow-x-auto mb-4">
            <table className="min-w-full border rounded-lg overflow-hidden text-sm">
              <tbody>
                {feature.table.map((row: string[], i: number) => (
                  <tr key={i} className={i === 0 ? "font-bold bg-primary/10" : ""}>
                    {row.map((cell, j) => (
                      <td key={j} className="px-3 py-2 border-b border-border text-center">{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Pie chart legend */}
        {feature.type === "pie" && (
          <div className="flex gap-2 mt-2 flex-wrap justify-center">
            {feature.pie.map((val: number, i: number) => (
              <span key={i} className="flex items-center gap-1 text-xs font-semibold text-primary">
                <span style={{ background: pieColors[i % pieColors.length] }} className="inline-block w-3 h-3 rounded-full mr-1"></span>
                {val}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function getFeatureIllustration(name: string) {
  switch (name) {
    case "Task Management":
      return (
        <svg width="100%" height="100%" viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="max-h-[110px] w-auto">
          <defs>
            <linearGradient id="tm-g1" x1="0" y1="0" x2="200" y2="150" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#60a5fa" />
              <stop offset="100%" stopColor="#3b82f6" />
            </linearGradient>
            <filter id="tm-shadow" x="-5" y="-5" width="210" height="160" filterUnits="userSpaceOnUse">
              <feDropShadow dx="2" dy="4" stdDeviation="4" floodOpacity="0.1" />
            </filter>
          </defs>
          <rect x="25" y="20" width="150" height="110" rx="12" fill="#ffffff" filter="url(#tm-shadow)" />
          <rect x="25" y="20" width="150" height="110" rx="12" fill="url(#tm-g1)" fillOpacity="0.05" stroke="#3b82f6" strokeWidth="1.5" />
          <rect x="45" y="40" width="20" height="20" rx="6" fill="#3b82f6" />
          <path d="M50 50L54 54L60 46" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          <rect x="75" y="44" width="80" height="12" rx="6" fill="#1e293b" />
          <rect x="45" y="70" width="20" height="20" rx="6" fill="#f1f5f9" stroke="#94a3b8" strokeWidth="2" />
          <rect x="75" y="74" width="60" height="12" rx="6" fill="#64748b" />
          <rect x="45" y="100" width="20" height="20" rx="6" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="2" />
          <rect x="75" y="104" width="70" height="12" rx="6" fill="#cbd5e1" />
          <circle cx="150" cy="80" r="16" fill="#3b82f6" />
          <path d="M142 90C142 84.5 145.5 82 150 82C154.5 82 158 84.5 158 90" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
          <circle cx="150" cy="74" r="5" fill="white" />
        </svg>
      );
    case "Project Management":
      return (
        <svg width="100%" height="100%" viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="max-h-[110px] w-auto">
          <defs>
            <linearGradient id="pm-g1" x1="0" y1="0" x2="200" y2="150" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#34d399" />
              <stop offset="100%" stopColor="#059669" />
            </linearGradient>
            <filter id="pm-shadow" x="-5" y="-5" width="210" height="160" filterUnits="userSpaceOnUse">
              <feDropShadow dx="2" dy="4" stdDeviation="4" floodOpacity="0.1" />
            </filter>
          </defs>
          <rect x="20" y="20" width="160" height="110" rx="12" fill="white" filter="url(#pm-shadow)" stroke="#10b981" strokeWidth="1.5" />
          <line x1="60" y1="20" x2="60" y2="130" stroke="#f1f5f9" strokeWidth="1.5" />
          <line x1="100" y1="20" x2="100" y2="130" stroke="#f1f5f9" strokeWidth="1.5" />
          <line x1="140" y1="20" x2="140" y2="130" stroke="#f1f5f9" strokeWidth="1.5" />
          <rect x="30" y="40" width="60" height="16" rx="8" fill="url(#pm-g1)" />
          <circle cx="90" cy="48" r="4" fill="white" />
          <rect x="70" y="68" width="80" height="16" rx="8" fill="#10b981" fillOpacity="0.4" stroke="#10b981" strokeWidth="1" />
          <rect x="70" y="68" width="45" height="16" rx="8" fill="#10b981" />
          <rect x="110" y="96" width="55" height="16" rx="8" fill="#d1fae5" stroke="#10b981" strokeWidth="1" />
          <circle cx="165" cy="104" r="6" fill="#f59e0b" />
          <path d="M165 101L168 104L165 107L162 104Z" fill="white" />
        </svg>
      );
    case "Organization chart":
      return (
        <svg width="100%" height="100%" viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="max-h-[110px] w-auto">
          <defs>
            <filter id="oc-shadow" x="-5" y="-5" width="210" height="160" filterUnits="userSpaceOnUse">
              <feDropShadow dx="2" dy="4" stdDeviation="4" floodOpacity="0.1" />
            </filter>
          </defs>
          <rect x="75" y="15" width="50" height="24" rx="6" fill="#fbbf24" filter="url(#oc-shadow)" />
          <circle cx="100" cy="27" r="6" fill="white" />
          <path d="M100 39V64M50 64H150M50 64V84M150 64V84" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
          <rect x="25" y="84" width="50" height="24" rx="6" fill="#3b82f6" filter="url(#oc-shadow)" />
          <circle cx="50" cy="96" r="6" fill="white" />
          <rect x="125" y="84" width="50" height="24" rx="6" fill="#3b82f6" filter="url(#oc-shadow)" />
          <circle cx="150" cy="96" r="6" fill="white" />
          <path d="M50 108V120H90" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
          <rect x="90" y="108" width="45" height="24" rx="6" fill="#10b981" filter="url(#oc-shadow)" />
          <circle cx="112" cy="120" r="6" fill="white" />
        </svg>
      );
    case "Employee KRA/KPI":
      return (
        <svg width="100%" height="100%" viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="max-h-[110px] w-auto">
          <defs>
            <linearGradient id="kpi-g1" x1="0" y1="0" x2="0" y2="150" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#c084fc" />
              <stop offset="100%" stopColor="#8b5cf6" />
            </linearGradient>
          </defs>
          <circle cx="100" cy="75" r="48" fill="#f5f3ff" stroke="#ddd6fe" strokeWidth="2" />
          <circle cx="100" cy="75" r="34" fill="white" stroke="#c084fc" strokeWidth="1.5" />
          <circle cx="100" cy="75" r="20" fill="url(#kpi-g1)" />
          <path d="M135 40L108 67M108 67L109 60M108 67L115 68" stroke="#7c3aed" strokeWidth="3" strokeLinecap="round" />
          <rect x="15" y="25" width="40" height="20" rx="6" fill="#818cf8" />
          <text x="35" y="38" fill="white" fontSize="9" fontWeight="bold" textAnchor="middle">KRA</text>
          <rect x="140" y="105" width="40" height="20" rx="6" fill="#34d399" />
          <text x="160" y="118" fill="white" fontSize="9" fontWeight="bold" textAnchor="middle">95%</text>
        </svg>
      );
    case "Company Growth":
      return (
        <svg width="100%" height="100%" viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="max-h-[110px] w-auto">
          <defs>
            <linearGradient id="cg-g1" x1="0" y1="0" x2="0" y2="150" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#f472b6" />
              <stop offset="100%" stopColor="#db2777" />
            </linearGradient>
          </defs>
          <line x1="30" y1="120" x2="170" y2="120" stroke="#e2e8f0" strokeWidth="1.5" />
          <line x1="30" y1="85" x2="170" y2="85" stroke="#f1f5f9" strokeWidth="1" />
          <line x1="30" y1="50" x2="170" y2="50" stroke="#f1f5f9" strokeWidth="1" />
          <rect x="40" y="90" width="16" height="30" rx="4" fill="#fbcfe8" />
          <rect x="70" y="70" width="16" height="50" rx="4" fill="#f472b6" />
          <rect x="100" y="55" width="16" height="65" rx="4" fill="#db2777" />
          <rect x="130" y="35" width="16" height="85" rx="4" fill="url(#cg-g1)" />
          <path d="M48 95C78 60 100 65 138 28" stroke="#be185d" strokeWidth="3.5" strokeLinecap="round" />
          <path d="M130 27H140V37" stroke="#be185d" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "Reports":
      return (
        <svg width="100%" height="100%" viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="max-h-[110px] w-auto">
          <defs>
            <filter id="rep-shadow" x="-5" y="-5" width="210" height="160" filterUnits="userSpaceOnUse">
              <feDropShadow dx="2" dy="4" stdDeviation="4" floodOpacity="0.1" />
            </filter>
          </defs>
          <rect x="35" y="25" width="80" height="105" rx="8" fill="white" filter="url(#rep-shadow)" stroke="#f97316" strokeWidth="1.5" />
          <rect x="60" y="15" width="30" height="15" rx="4" fill="#fb923c" />
          <rect x="48" y="45" width="40" height="8" rx="4" fill="#fb923c" />
          <rect x="48" y="60" width="55" height="6" rx="3" fill="#cbd5e1" />
          <rect x="48" y="72" width="45" height="6" rx="3" fill="#cbd5e1" />
          <circle cx="135" cy="95" r="32" fill="white" filter="url(#rep-shadow)" stroke="#e2e8f0" strokeWidth="2" />
          <path d="M135 95 L135 63 A32 32 0 0 1 167 95 Z" fill="#fb923c" />
          <path d="M135 95 L167 95 A32 32 0 0 1 135 127 Z" fill="#fdba74" />
          <path d="M135 95 L135 127 A32 32 0 0 1 103 95 Z" fill="#fdd8e5" opacity="0.5" />
          <path d="M135 95 L103 95 A32 32 0 0 1 135 63 Z" fill="#fed7aa" />
        </svg>
      );
    case "Task Delegation":
      return (
        <svg width="100%" height="100%" viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="max-h-[110px] w-auto">
          <circle cx="100" cy="45" r="20" fill="#0ea5e9" />
          <path d="M90 58C90 51.5 94.5 49 100 49C105.5 49 110 51.5 110 58" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="100" cy="38" r="6" fill="white" />
          <path d="M75 55L45 80" stroke="#94a3b8" strokeWidth="2" strokeDasharray="4 3" strokeLinecap="round" />
          <path d="M45 80H53V72" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
          <path d="M125 55L155 80" stroke="#94a3b8" strokeWidth="2" strokeDasharray="4 3" strokeLinecap="round" />
          <path d="M155 80H147V72" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
          <circle cx="35" cy="100" r="16" fill="#10b981" />
          <path d="M27 110C27 105 30.5 103 35 103C39.5 103 43 105 43 110" stroke="white" strokeWidth="2" />
          <circle cx="35" cy="94" r="5" fill="white" />
          <circle cx="165" cy="100" r="16" fill="#f59e0b" />
          <path d="M157 110C157 105 160.5 103 165 103C169.5 103 173 105 173 110" stroke="white" strokeWidth="2" />
          <circle cx="165" cy="94" r="5" fill="white" />
        </svg>
      );
    case "To Do List":
      return (
        <svg width="100%" height="100%" viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="max-h-[110px] w-auto">
          <defs>
            <filter id="todo-shadow" x="-5" y="-5" width="210" height="160" filterUnits="userSpaceOnUse">
              <feDropShadow dx="2" dy="4" stdDeviation="4" floodOpacity="0.1" />
            </filter>
          </defs>
          <rect x="40" y="25" width="120" height="100" rx="8" fill="white" filter="url(#todo-shadow)" stroke="#6366f1" strokeWidth="2" />
          <circle cx="55" cy="25" r="4" fill="#a5b4fc" />
          <circle cx="80" cy="25" r="4" fill="#a5b4fc" />
          <circle cx="105" cy="25" r="4" fill="#a5b4fc" />
          <circle cx="130" cy="25" r="4" fill="#a5b4fc" />
          <circle cx="145" cy="25" r="4" fill="#a5b4fc" />
          <circle cx="60" cy="55" r="8" fill="#e0e7ff" stroke="#6366f1" strokeWidth="1.5" />
          <path d="M57 55L59 57L63 53" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" />
          <line x1="80" y1="55" x2="140" y2="55" stroke="#1e293b" strokeWidth="2" strokeLinecap="round" />
          <circle cx="60" cy="85" r="8" fill="#e0e7ff" stroke="#6366f1" strokeWidth="1.5" />
          <path d="M57 85L59 87L63 83" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" />
          <line x1="80" y1="85" x2="130" y2="85" stroke="#1e293b" strokeWidth="2" strokeLinecap="round" />
          <circle cx="60" cy="115" r="8" fill="white" stroke="#cbd5e1" strokeWidth="1.5" />
          <line x1="80" y1="115" x2="120" y2="115" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    case "Task Tracker":
      return (
        <svg width="100%" height="100%" viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="max-h-[110px] w-auto">
          <circle cx="100" cy="75" r="45" fill="white" stroke="#e2e8f0" strokeWidth="8" />
          <circle cx="100" cy="75" r="45" fill="none" stroke="#10b981" strokeWidth="8" strokeDasharray="283" strokeDashoffset="80" strokeLinecap="round" transform="rotate(-90 100 75)" />
          <text x="100" y="82" fill="#1e293b" fontSize="20" fontWeight="bold" textAnchor="middle">72%</text>
          <circle cx="145" cy="45" r="14" fill="#10b981" />
          <path d="M141 45L144 48L150 42" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      );
    default:
      return null;
  }
}

function FeatureCard({ feature, index, onClick }: { feature: any; index: number; onClick: () => void }) {
  const lightColors = [
    "bg-gradient-to-br from-indigo-50/50 to-blue-50/50 hover:from-indigo-100 hover:to-blue-100 border-indigo-100",
    "bg-gradient-to-br from-emerald-50/50 to-teal-50/50 hover:from-emerald-100 hover:to-teal-100 border-emerald-100",
    "bg-gradient-to-br from-amber-50/50 to-orange-50/50 hover:from-amber-100 hover:to-orange-100 border-amber-100",
    "bg-gradient-to-br from-purple-50/50 to-fuchsia-50/50 hover:from-purple-100 hover:to-fuchsia-100 border-purple-100",
    "bg-gradient-to-br from-pink-50/50 to-rose-50/50 hover:from-pink-100 hover:to-rose-100 border-pink-100",
    "bg-gradient-to-br from-orange-50/50 to-red-50/50 hover:from-orange-100 hover:to-red-100 border-orange-100",
    "bg-gradient-to-br from-sky-50/50 to-cyan-50/50 hover:from-sky-100 hover:to-cyan-100 border-sky-100",
    "bg-gradient-to-br from-blue-50/50 to-indigo-50/50 hover:from-blue-100 hover:to-indigo-100 border-blue-100",
    "bg-gradient-to-br from-violet-50/50 to-purple-50/50 hover:from-violet-100 hover:to-purple-100 border-violet-100",
    "bg-gradient-to-br from-teal-50/50 to-emerald-50/50 hover:from-teal-100 hover:to-emerald-100 border-teal-100"
  ];
  const borderColors = [
    "group-hover:border-indigo-400 group-hover:shadow-[0_0_15px_rgba(99,102,241,0.25)]",
    "group-hover:border-emerald-400 group-hover:shadow-[0_0_15px_rgba(16,185,129,0.25)]",
    "group-hover:border-amber-400 group-hover:shadow-[0_0_15px_rgba(245,158,11,0.25)]",
    "group-hover:border-purple-400 group-hover:shadow-[0_0_15px_rgba(168,85,247,0.25)]",
    "group-hover:border-pink-400 group-hover:shadow-[0_0_15px_rgba(236,72,153,0.25)]",
    "group-hover:border-orange-400 group-hover:shadow-[0_0_15px_rgba(249,115,22,0.25)]",
    "group-hover:border-sky-400 group-hover:shadow-[0_0_15px_rgba(14,165,233,0.25)]",
    "group-hover:border-blue-400 group-hover:shadow-[0_0_15px_rgba(59,130,246,0.25)]",
    "group-hover:border-violet-400 group-hover:shadow-[0_0_15px_rgba(139,92,246,0.25)]",
    "group-hover:border-teal-400 group-hover:shadow-[0_0_15px_rgba(20,184,166,0.25)]"
  ];
  const bgClass = lightColors[index % lightColors.length];
  const borderColorClass = borderColors[index % borderColors.length];

  return (
    <button
      className={`${bgClass} rounded-2xl border shadow-sm p-6 flex flex-col items-center justify-start text-primary hover:scale-[1.03] hover:shadow-xl transition-all duration-300 ease-out text-center animate-fade-in group w-full h-[360px]`}
      onClick={onClick}
    >
      <div className={`w-full aspect-[4/3] rounded-xl mb-4 bg-white/80 backdrop-blur-sm border border-slate-100 flex items-center justify-center overflow-hidden shadow-inner group-hover:bg-white transition-all duration-300 ${borderColorClass}`}>
        {getFeatureIllustration(feature.name)}
      </div>
      <h3 className="font-bold text-lg mb-2 text-slate-800 group-hover:text-primary transition-colors duration-200">{feature.name}</h3>
      <p className="text-sm text-slate-500 font-medium leading-relaxed mb-4 line-clamp-2">{feature.desc}</p>
      <span className="mt-auto text-xs font-bold text-accent/80 group-hover:text-accent flex items-center gap-1.5 transition-colors duration-200">
        Try Interactive Demo 
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-3.5 h-3.5 transform group-hover:translate-x-1 transition-transform duration-200">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>
      </span>
    </button>
  );
}

export default function ProductMainPage() {
  const [selectedFeature, setSelectedFeature] = useState<any | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [subtaskModalOpen, setSubtaskModalOpen] = useState(false);
  const featuresRef = useRef<HTMLDivElement>(null);
  const industriesRef = useRef<HTMLDivElement>(null);
  const pricingRef = useRef<HTMLDivElement>(null);
  const blogRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);

  const scrollTo = (ref: React.RefObject<HTMLDivElement | null>) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth" });
      setIsMenuOpen(false); // Close menu on scroll
    }
  };

  return (
    <main className="min-h-screen bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-pink-100 via-indigo-50 to-cyan-100 flex flex-col relative overflow-x-clip">
      {/* Decorative Background Shapes */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none z-0">
        <div
          className="absolute top-[-80px] left-[-120px] w-[400px] h-[400px] bg-purple-400/30 rounded-full blur-3xl mix-blend-multiply"
          style={{ transform: "translate3d(0, 0, 0)", willChange: "transform, filter" }}
        />
        <div
          className="absolute bottom-[-120px] right-[-120px] w-[400px] h-[400px] bg-cyan-400/30 rounded-full blur-3xl mix-blend-multiply"
          style={{ transform: "translate3d(0, 0, 0)", willChange: "transform, filter" }}
        />
        <div
          className="absolute top-1/4 left-1/2 w-[300px] h-[300px] bg-pink-400/30 rounded-full blur-3xl mix-blend-multiply"
          style={{ transform: "translate3d(0, 0, 0)", willChange: "transform, filter" }}
        />
      </div>
      {/* Header */}
      <header
        className="w-full bg-white/90 border-b border-border shadow-lg flex items-center justify-between px-6 md:px-8 py-4 sticky top-0 z-20 backdrop-blur-md"
        style={{ transform: "translate3d(0, 0, 0)", willChange: "transform" }}
      >
        <Link href="/#" className="flex items-center gap-2 md:gap-3">
          <Image src={logoImg} alt="NextGenTask Logo" className="w-8 h-8 md:w-10 md:h-10 rounded-lg shadow-sm object-cover" unoptimized={true} />
          <span className="text-xl md:text-2xl font-bold text-primary">NextGenTask</span>
          <span className="text-[10px] md:text-xs font-semibold text-success md:ml-2">BE ORGANIZED</span>
        </Link>

        {/* Mobile Toggle */}
        <button
          className="md:hidden p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
            {isMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            )}
          </svg>
        </button>

        {/* Desktop Nav */}
        <nav className="hidden md:flex gap-6 lg:gap-8 items-center">
          <button className="text-lg font-semibold text-primary hover:text-accent transition" onClick={() => scrollTo(featuresRef)}>Features</button>
          <button className="text-lg font-semibold text-primary hover:text-accent transition" onClick={() => scrollTo(industriesRef)}>Industries</button>
          <Link href="/signup" className="text-lg font-semibold text-primary hover:text-accent transition">Pricing</Link>
          <button className="text-lg font-semibold text-primary hover:text-accent transition" onClick={() => scrollTo(blogRef)}>Blog</button>
          <button className="text-lg font-semibold text-primary hover:text-accent transition" onClick={() => scrollTo(contactRef)}>Contact Us</button>
          <Link href="/signup">
            <button className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white font-semibold text-base px-6 py-2 rounded-full shadow-md hover:shadow-lg hover:scale-105 transition">Demo</button>
          </Link>
          <Link href="/login">
            <button className="border border-primary text-primary font-semibold text-base px-6 py-2 rounded-full hover:bg-primary/10 transition">Account</button>
          </Link>
        </nav>

        {/* Mobile Nav Overlay */}
        {isMenuOpen && (
          <div className="absolute top-full left-0 w-full bg-white border-b border-border shadow-xl md:hidden animate-in fade-in slide-in-from-top-4 duration-300">
            <div className="flex flex-col p-6 gap-4">
              <button className="text-left py-2 px-4 text-xl font-bold text-primary hover:bg-primary/5 rounded-xl transition" onClick={() => scrollTo(featuresRef)}>Features</button>
              <button className="text-left py-2 px-4 text-xl font-bold text-primary hover:bg-primary/5 rounded-xl transition" onClick={() => scrollTo(industriesRef)}>Industries</button>
              <Link href="/signup" className="py-2 px-4 text-xl font-bold text-primary hover:bg-primary/5 rounded-xl transition" onClick={() => setIsMenuOpen(false)}>Pricing</Link>
              <button className="text-left py-2 px-4 text-xl font-bold text-primary hover:bg-primary/5 rounded-xl transition" onClick={() => scrollTo(blogRef)}>Blog</button>
              <button className="text-left py-2 px-4 text-xl font-bold text-primary hover:bg-primary/5 rounded-xl transition" onClick={() => scrollTo(contactRef)}>Contact Us</button>
              <div className="flex flex-col gap-3 mt-4">
                <Link href="/signup" onClick={() => setIsMenuOpen(false)}>
                  <button className="w-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white font-bold py-3 rounded-xl shadow-md">Get Demo</button>
                </Link>
                <Link href="/login" onClick={() => setIsMenuOpen(false)}>
                  <button className="w-full border-2 border-primary text-primary font-bold py-3 rounded-xl hover:bg-primary/10">Account Login</button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>
      {/* Hero Banner Image Section — 50vh fixed height */}
      <section className="relative w-full overflow-hidden z-10" style={{ height: '50vh', minHeight: '320px', maxHeight: '600px' }}>
        {/* Banner Image — shows lower rooftop scene only */}
        <Image
          src={heroBannerImg}
          alt="NextGenTask — Designed to simplify your life"
          fill
          style={{ objectFit: 'cover', objectPosition: 'center 62%' }}
          className="scale-[1.8] -translate-x-[10%] origin-bottom md:scale-100 md:translate-x-0"
          priority
          unoptimized
        />

        {/* Gradient — covers left side for headline text and bottom area to mask image text */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(to right, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.70) 25%, rgba(0,0,0,0.30) 42%, transparent 58%)' }}
        />
        {/* Dark bottom gradient overlay to completely obscure embedded image text */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.95) 15%, rgba(0,0,0,0.60) 28%, transparent 45%)' }}
        />

        {/* Animated shimmer border at the bottom */}
        <div
          className="absolute bottom-0 left-0 w-full h-[3px] pointer-events-none"
          style={{
            background: 'linear-gradient(90deg, #6366f1, #a855f7, #ec4899, #6366f1)',
            backgroundSize: '200% 100%',
            animation: 'shimmer-banner 3s linear infinite',
          }}
        />

        {/* Live Platform badge — top-left */}
        <div className="absolute top-4 left-5 md:top-5 md:left-8 flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/25 rounded-full px-4 py-1.5 shadow-lg">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-white text-xs font-bold tracking-widest uppercase">Live Platform</span>
        </div>

        {/* Left-side headline — sits on the dark gradient area */}
        <div className="absolute inset-y-0 left-0 flex flex-col justify-center pl-7 md:pl-12 max-w-[55%] md:max-w-lg">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight mb-3" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.5)' }}>
            Designed to{' '}
            <span className="text-indigo-300">simplify</span><br />
            your life
          </h2>
          <p className="text-white/90 text-sm md:text-base font-medium leading-relaxed max-w-sm" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.6)' }}>
            Focus on what matters with{' '}
            <span className="text-indigo-300 font-semibold">intelligent collaboration</span>{' '}
            and automation from NextGenTask
          </p>

        </div>
      </section>

      {/* Hero Section */}
      <section className="flex flex-col items-center justify-center w-full max-w-6xl mx-auto pt-24 pb-16 px-6 gap-8 relative z-10 text-center">
        <div className="w-full max-w-3xl flex flex-col items-center">
          <div className="mb-6 py-2 px-4 rounded-full bg-gradient-to-r from-indigo-50 via-purple-50 to-indigo-50 border border-indigo-200/60 animate-fade-in shadow-sm inline-flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></span>
            <span className="text-xs sm:text-sm font-semibold text-indigo-700 tracking-wide">NextGen Philosophy</span>
          </div>
          
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-extrabold bg-gradient-to-r from-emerald-600 to-teal-500 bg-clip-text text-transparent mb-6 leading-tight drop-shadow-sm animate-fade-in pb-2">
            NextGenTask Manager
          </h1>
          
          <h2 className="text-xl sm:text-2xl md:text-3xl font-black italic bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-6 animate-fade-in">
            &ldquo;One Platform for Tasks, Talent, and Growth.&rdquo;
          </h2>
          
          <p className="text-lg sm:text-xl text-slate-600 mb-10 max-w-2xl animate-fade-in leading-relaxed">
            While traditional tools focus only on tracking work, NextGenTask connects tasks, performance, career development, and business outcomes into a single ecosystem.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center gap-4 animate-fade-in">
            <Link href="/signup">
              <button className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white font-bold px-8 py-3.5 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all text-lg w-full sm:w-auto">
                Get Started for Free
              </button>
            </Link>
            <Link href="/signup">
              <button className="bg-white text-indigo-600 font-bold px-8 py-3.5 rounded-full shadow-md hover:shadow-lg border border-indigo-100 hover:bg-indigo-50 transition-all text-lg w-full sm:w-auto">
                Request a Demo
              </button>
            </Link>
          </div>
        </div>

        {/* Visual Dashboard Mockup - Centered Below Hero Text */}
        <div className="w-full max-w-5xl mx-auto mt-16 animate-fade-in relative z-20" style={{ perspective: '1000px' }}>
          <div className="w-full bg-white rounded-xl shadow-[0_20px_50px_rgba(8,_112,_184,_0.1)] border border-slate-200 overflow-hidden transform hover:-translate-y-2 transition-transform duration-500">
            <img 
              src={dashboardImg.src} 
              alt="NextGenTask Manager Dashboard" 
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
      </section>

      {/* Core Concept Detailed Explanation */}
      <section className="w-full max-w-6xl mx-auto py-16 px-6 relative z-10 border-t border-slate-200/50 mt-10">
        <div className="flex flex-col md:flex-row gap-12 lg:gap-20">
          
          {/* Left Column: Key Highlights */}
          <div className="flex-1">
            <div className="inline-block px-4 py-1.5 rounded-full bg-indigo-100/50 text-indigo-700 text-sm font-bold uppercase tracking-wider shadow-sm mb-6">
              Core Capabilities
            </div>
            <h3 className="text-3xl font-extrabold text-primary mb-8">Key Highlights</h3>
            
            <div className="space-y-8">
              <div className="flex items-start gap-4 group">
                <span className="flex-shrink-0 w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors duration-300">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                </span>
                <div>
                  <h4 className="font-bold text-lg text-slate-800 mb-2">Smart Task & Project Management</h4>
                  <p className="text-slate-600 leading-relaxed text-sm">
                    Plan, assign, track, and deliver tasks with complete visibility. Manage projects, deadlines, dependencies, and team workloads efficiently.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <span className="flex-shrink-0 w-12 h-12 rounded-2xl bg-purple-50 border border-purple-100 flex items-center justify-center text-purple-600 group-hover:bg-purple-600 group-hover:text-white transition-colors duration-300">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                </span>
                <div>
                  <h4 className="font-bold text-lg text-slate-800 mb-2">Employee Career Progress Tracking</h4>
                  <p className="text-slate-600 leading-relaxed text-sm">
                    Monitor employee achievements, skills, certifications, KRAs, KPIs, and growth milestones. Create clear career progression paths.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <span className="flex-shrink-0 w-12 h-12 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-600 group-hover:bg-amber-600 group-hover:text-white transition-colors duration-300">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                </span>
                <div>
                  <h4 className="font-bold text-lg text-slate-800 mb-2">Performance-Driven Workforce</h4>
                  <p className="text-slate-600 leading-relaxed text-sm">
                    Measure productivity through real-time performance insights. Connect individual contributions directly to organizational goals.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Why NextGenTask */}
          <div className="flex-1 bg-white/50 backdrop-blur-sm rounded-3xl p-8 lg:p-12 border border-slate-200/50 shadow-xl shadow-slate-200/20 flex flex-col justify-center">
            <h3 className="text-3xl font-extrabold mb-6" style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '-0.02em' }}>
              Why NextGenTask<span style={{ WebkitTextFillColor: '#ec4899' }}>?</span>
            </h3>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6 font-medium">
              Because organizations don&apos;t grow through completed tasks alone—they grow through <span className="font-bold text-indigo-600">empowered people</span>, <span className="font-bold text-purple-600">satisfied customers</span>, and <span className="font-bold text-pink-600">measurable business outcomes</span>.
            </p>
            
            <div className="w-full h-px bg-gradient-to-r from-indigo-200 via-purple-200 to-transparent mb-6"></div>
            
            <p className="text-base leading-relaxed text-slate-600 italic border-l-4 border-indigo-300 pl-4 py-1">
              NextGenTask bridges the gap between <span className="not-italic font-bold text-slate-800">Work Management</span>, <span className="not-italic font-bold text-slate-800">Employee Development</span>, and <span className="not-italic font-bold text-slate-800">Business Growth</span>, enabling organizations to scale smarter and faster.
            </p>
            
            <div className="mt-10">
              <Link href="/signup">
                <span className="inline-flex items-center gap-2 font-bold text-indigo-600 hover:text-indigo-800 transition-colors group cursor-pointer">
                  See how it works 
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5 transform group-hover:translate-x-2 transition-transform duration-300">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </span>
              </Link>
            </div>
          </div>
          
        </div>
      </section>
      {/* Features Section */}
      <section ref={featuresRef} id="features" className="w-full py-20 bg-gradient-to-b from-transparent via-purple-50/60 to-transparent overflow-hidden relative border-y border-slate-100/50">
        <div className="max-w-6xl mx-auto px-6 mb-12 text-center relative z-10">
          <h2 className="text-4xl font-bold text-primary mb-2 drop-shadow-lg animate-fade-in text-center">Features</h2>
          <p className="text-slate-500 max-w-2xl mx-auto animate-fade-in text-sm sm:text-base text-center">
            Discover how our tools help teams and businesses grow. Hover over any card to pause and click to explore its interactive visual representation.
          </p>
        </div>

        {/* Gradient overlays for fade-out effect on sides */}
        <div className="absolute inset-y-0 left-0 w-16 md:w-32 bg-gradient-to-r from-white via-white/80 to-transparent pointer-events-none z-10" />
        <div className="absolute inset-y-0 right-0 w-16 md:w-32 bg-gradient-to-l from-white via-white/80 to-transparent pointer-events-none z-10" />

        <div className="flex flex-col gap-8 w-full relative z-0">
          {/* Row 1: Left Moving */}
          <div className="relative w-full overflow-hidden flex items-center py-2">
            <div className="animate-marquee flex gap-6">
              {[...features.slice(0, 5), ...features.slice(0, 5)].map((feature, idx) => (
                <div key={`${feature.name}-r1-${idx}`} className="w-[300px] flex-shrink-0">
                  <FeatureCard feature={feature} index={idx} onClick={() => setSelectedFeature(feature)} />
                </div>
              ))}
            </div>
          </div>

          {/* Row 2: Right Moving */}
          <div className="relative w-full overflow-hidden flex items-center py-2">
            <div className="animate-marquee-reverse flex gap-6">
              {[...features.slice(5), ...features.slice(5)].map((feature, idx) => (
                <div key={`${feature.name}-r2-${idx}`} className="w-[300px] flex-shrink-0">
                  <FeatureCard feature={feature} index={idx} onClick={() => setSelectedFeature(feature)} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {selectedFeature && <FeatureModal feature={selectedFeature} onClose={() => setSelectedFeature(null)} />}

        <style>{`
          @keyframes marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          @keyframes marquee-reverse {
            0% { transform: translateX(-50%); }
            100% { transform: translateX(0); }
          }
          .animate-marquee {
            display: flex;
            width: max-content;
            animation: marquee 35s linear infinite;
          }
          .animate-marquee-reverse {
            display: flex;
            width: max-content;
            animation: marquee-reverse 35s linear infinite;
          }
          .animate-marquee:hover,
          .animate-marquee-reverse:hover {
            animation-play-state: paused;
          }
        `}</style>
      </section>
      {/* Industries Section */}
      <section ref={industriesRef} id="industries" className="w-full max-w-5xl mx-auto py-20 px-6">
        <div className="text-center mb-12">
          <span className="px-4 py-1.5 rounded-full bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 text-xs sm:text-sm font-bold uppercase tracking-wider shadow-sm">
            Versatile Applicability
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-800 mt-3 drop-shadow-md">
            Built for Industry Alignment
          </h2>
          <p className="text-slate-600 mt-2 max-w-2xl mx-auto text-base sm:text-lg text-center">
            Unlike generic software, NextGenTask adapts to the operational realities, team hierarchies, and performance metrics of your specific industry.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {industries.map((ind, idx) => (
            <div key={idx} className="flex gap-4 items-start group">
              <span className={`flex-shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br ${ind.color} flex items-center justify-center shadow-lg text-white group-hover:scale-110 transition-transform duration-300`}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d={ind.iconPath} />
                </svg>
              </span>
              <div className="flex-1 border-b border-slate-200 pb-6">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-bold text-lg text-slate-800">{ind.name}</h3>
                  <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{ind.desc}</span>
                </div>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed text-left">
                  {ind.fit}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>
      {/* Pricing Section (links to signup) */}
      <section ref={pricingRef} id="pricing" className="w-full max-w-4xl mx-auto py-16 px-4">
        <h2 className="text-3xl font-bold text-slate-800 mb-8 text-center drop-shadow-lg animate-fade-in">Pricing</h2>
        <div className="flex flex-col items-center">
          <p className="mb-6 text-lg text-slate-600 text-center animate-fade-in">See our flexible plans and get started for free.</p>
          <Link href="/signup">
            <button className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white font-bold px-10 py-4 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all animate-fade-in">View Pricing & Sign Up</button>
          </Link>
        </div>
      </section>
      {/* Blog Section */}
      <section ref={blogRef} id="blog" className="w-full max-w-6xl mx-auto py-16 px-4">
        <h2 className="text-4xl font-bold text-slate-800 mb-8 text-center drop-shadow-lg animate-fade-in">Blog</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Blog Post 1 */}
          <div className="bg-white/90 rounded-xl shadow-lg border border-slate-200 p-6 hover:scale-105 transition-all">
            <h3 className="text-xl font-semibold text-slate-800 mb-4">Boost Your Productivity</h3>
            <p className="text-sm text-slate-500 mb-6">Learn how NextGenTask Manager can help you streamline your workflows and achieve more in less time.</p>
            <button className="text-indigo-600 font-medium hover:underline">Read More</button>
          </div>
          {/* Blog Post 2 */}
          <div className="bg-white/90 rounded-xl shadow-lg border border-slate-200 p-6 hover:scale-105 transition-all">
            <h3 className="text-xl font-semibold text-slate-800 mb-4">Effective Team Collaboration</h3>
            <p className="text-sm text-slate-500 mb-6">Discover the best practices for fostering collaboration and communication within your team.</p>
            <button className="text-indigo-600 font-medium hover:underline">Read More</button>
          </div>
          {/* Blog Post 3 */}
          <div className="bg-white/90 rounded-xl shadow-lg border border-slate-200 p-6 hover:scale-105 transition-all">
            <h3 className="text-xl font-semibold text-slate-800 mb-4">All-in-One Task Management</h3>
            <p className="text-sm text-slate-500 mb-6">Explore how NextGenTask Manager combines all essential tools in one platform for seamless task management.</p>
            <button className="text-indigo-600 font-medium hover:underline">Read More</button>
          </div>
        </div>
      </section>
      {/* Contact Us Section */}
      <section ref={contactRef} id="contact" className="w-full max-w-4xl mx-auto py-16 px-4">
        <h2 className="text-3xl font-bold text-slate-800 mb-8 text-center drop-shadow-lg animate-fade-in">Contact Us</h2>
        <div className="flex flex-col items-center gap-4">
          <p className="mb-2 text-lg text-slate-600 text-center animate-fade-in">For inquiries, partnerships, or support, email us at <a href="mailto:support@qvs.co.in" className="text-indigo-600 underline">support@qvs.co.in</a></p>
          <a
            href="https://www.linkedin.com/company/111226900/admin/page-posts/published/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#0077b5] text-white font-semibold hover:bg-[#0077b5]/90 transition shadow-md hover:shadow-lg animate-fade-in"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.8v8.37h2.8v-4.67c0-.25.02-.5.1-.68a1.14 1.14 0 0 1 1-.77c.76 0 1 .52 1 1.29v4.83h2.78M6.5 8.37a1.37 1.37 0 0 0 1.3-1.3A1.3 1.3 0 0 0 6.5 5.8a1.3 1.3 0 0 0-1.3 1.3a1.37 1.37 0 0 0 1.3 1.27m1.4 10.13V10.1h-2.8v8.4h2.8z"/>
            </svg>
            Follow us on LinkedIn
          </a>
        </div>
      </section>

      <PublicFooter />

    </main>
  );
}
